import { ROUTES } from "@app/constants";
import { useGetBrIefById } from "@entities/brief/lib/hooks/useGetBriefById";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import useGetPricingWorkloads from "@entities/pricingWorkload/lib/hooks/useGetPricingWorkloads";
import { ClosureDetailCreatorsSection } from "@features/closureDetail/ui/ClosureDetailCreatorsSection";
import { OutreachTerms } from "@features/OutreachTerms";
import { UnifiedCampaignSetup } from "@features/UnifiedCampaignSetup";
import { getClosureDetailPath } from "@pages/Closures/lib/utils/routes";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { Card, Typography } from "@shared/ui";
import { Select } from "@shared/ui/Select";
import { PageHeader } from "@widgets/PageHeader";
import {
  ProjectWorkspaceTabs,
  type ProjectWorkspaceTab,
} from "@widgets/ProjectWorkspaceTabs";
import { CircleUser } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./styles.module.scss";

const { Title, Text } = Typography;

export function ClosureDetailPage() {
  const navigate = useNavigate();
  const { briefId, userId: userIdParam } = useParams<{
    briefId: string;
    userId: string;
  }>();
  const { setBrief, setBriefIdOnly } = useBriefStore();
  const { brief } = useGetBrIefById(briefId || "");
  const { userDetails, isSuccess } = useGetMe();
  const currentUserId = userDetails?.userEntity?.id ?? "";

  const isSuperUser = Boolean(
    userDetails?.userEntity?.permissions?.includes("super")
  );

  const { pricingWorkloadData, isPricingWorkloadLoading } =
    useGetPricingWorkloads({
      userIds: [],
      enabled: isSuperUser,
    });

  const { pricingWorkloadData: pricingWorkloadPerUser } =
    useGetPricingWorkloads({
      userIds: userIdParam ? [userIdParam] : [],
      enabled: Boolean(userIdParam),
    });

  const selectedPoc = useMemo(() => {
    return (
      pricingWorkloadPerUser?.data?.find(
        (item) => item.userId === userIdParam
      ) || null
    );
  }, [pricingWorkloadPerUser, userIdParam]);

  useEffect(() => {
    if (!isSuccess || !currentUserId || !briefId || userIdParam) {
      return;
    }

    navigate(getClosureDetailPath(briefId, currentUserId), { replace: true });
  }, [isSuccess, currentUserId, userIdParam, briefId, navigate]);

  useEffect(() => {
    if (briefId) {
      setBriefIdOnly(briefId);
    }
  }, [briefId, setBriefIdOnly]);

  useEffect(() => {
    if (brief && briefId) {
      setBrief(brief, briefId);
      document.title = brief.name;
      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );
      if (metaDescription) {
        metaDescription.setAttribute("content", brief.name);
      } else {
        const newMetaDescription = document.createElement("meta");
        newMetaDescription.name = "description";
        newMetaDescription.content = brief.name;
        document.head.appendChild(newMetaDescription);
      }
    }
    return () => {
      document.title = "Cloutflow";
      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );
      if (metaDescription) {
        metaDescription.setAttribute("content", "Cloutflow");
      } else {
        const newMetaDescription = document.createElement("meta");
        newMetaDescription.name = "description";
        newMetaDescription.content = "Cloutflow";
        document.head.appendChild(newMetaDescription);
      }
    };
  }, [brief, briefId, setBrief]);

  const handlePocChange = (userId: string) => {
    if (!briefId) {
      return;
    }

    navigate(getClosureDetailPath(briefId, userId));
  };

  const [workspaceTab, setWorkspaceTab] =
    useState<ProjectWorkspaceTab>("creators");

  return (
    <div className={styles.closureDetailPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <PageHeader
            title={brief?.name || "Project"}
            backText="My Closures"
            backTo={`${ROUTES.CLOSURES}?userId=${userIdParam}`}
          />
          <Title level={2} className={styles.pageTitle}>
            {brief?.name || "Project"}
          </Title>
          {brief?.brand?.name ? (
            <Text type="secondary" className={styles.pageDescription}>
              {brief.brand.name}
            </Text>
          ) : null}
        </div>

        <div className={styles.headerRightActions}>
          {isSuperUser ? (
            <Card
              className={styles.pocCard}
              title={
                <span className={styles.pocLabel}>
                  <CircleUser size={14} />
                  <Text type="secondary">Working as POC</Text>
                </span>
              }
            >
              {pricingWorkloadData ? (
                <Select
                  options={pricingWorkloadData.data?.map((data) => ({
                    label: `${data?.user?.firstName} ${data?.user?.lastName}`,
                    value: data?.user?._id,
                  }))}
                  loading={isPricingWorkloadLoading}
                  showSearch
                  optionFilterProp="label"
                  value={
                    pricingWorkloadData.data?.find(
                      (val) => val.userId === userIdParam
                    )
                      ? userIdParam
                      : null
                  }
                  onChange={handlePocChange}
                  style={{ width: "100%" }}
                  placeholder="Select a Pricing POC"
                />
              ) : null}
              {selectedPoc?.user?.emailId ? (
                <Text type="secondary" className={styles.pocEmail}>
                  {selectedPoc.user.emailId}
                </Text>
              ) : null}
            </Card>
          ) : null}
          <OutreachTerms terms={brief?.pricingOutreachTerms} />
        </div>
      </div>

      <ProjectWorkspaceTabs
        activeTab={workspaceTab}
        onTabChange={setWorkspaceTab}
      />

      {workspaceTab === "overview" ? (
        <UnifiedCampaignSetup
          showCampaignName={false}
          showOutreachTerms={false}
        />
      ) : briefId && userIdParam ? (
        <ClosureDetailCreatorsSection
          briefId={briefId}
          userId={userIdParam}
          brief={brief}
        />
      ) : null}
    </div>
  );
}
